<template>
  <v-container fluid>
    <v-card class="elevation-2">
      <v-toolbar color="primary" dark flat>
        <v-icon class="mr-2">mdi-account-hard-hat</v-icon>
        <v-toolbar-title>Colaboradores</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-col cols="3" class="mr-3" v-if="$vuetify.breakpoint.mdAndUp">
          <v-select v-model="dealerFilter" :items="dealers" item-text="name" item-value="id" label="Filtrar por dealer"
            clearable hide-details rounded filled dense dark @change="load"></v-select>
        </v-col>
        <v-btn icon @click="openForm(null)" title="Nuevo colaborador"><v-icon>mdi-plus</v-icon></v-btn>
        <v-btn icon @click="load" title="Actualizar"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-toolbar>

      <v-data-table :headers="headers" :items="colaboradores" :loading="loading" item-key="id" :items-per-page="10" :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
        no-data-text="No hay colaboradores">
        <template v-slot:item.colaborador_type="{ item }">{{ colaboradorTypeLabel(item.colaborador_type) }}</template>
        <template v-slot:item.conditions="{ item }">
          <v-chip v-for="c in item.conditions" :key="c" x-small class="mr-1" color="primary" outlined>{{ conditionLabel(c) }}</v-chip>
          <span v-if="!item.conditions.length" class="grey--text text-caption">Sin condiciones</span>
        </template>
        <template v-slot:item.dealers="{ item }">
          <v-chip v-for="d in item.dealers" :key="d.id" x-small class="mr-1 mb-1">{{ d.name }}</v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="item.status === STATUS_ACTIVE ? 'success' : 'grey'" dark>
            {{ item.status === STATUS_ACTIVE ? 'Activo' : 'Deshabilitado' }}
          </v-chip>
        </template>
        <template v-slot:item.action="{ item }">
          <div class="d-inline-flex">
            <v-btn icon small color="info" @click="viewDetails(item)" title="Ver detalles"><v-icon>mdi-eye</v-icon></v-btn>
            <v-btn icon small color="primary" @click="openForm(item)" title="Editar"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn icon small :color="item.status === STATUS_ACTIVE ? 'error' : 'success'" @click="toggleStatus(item)"
              :title="item.status === STATUS_ACTIVE ? 'Deshabilitar colaborador' : 'Habilitar colaborador'">
              <v-icon>{{ item.status === STATUS_ACTIVE ? 'mdi-cancel' : 'mdi-check-circle' }}</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="640px" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-account-hard-hat</v-icon>
          {{ editing ? 'Editar colaborador' : 'Nuevo colaborador' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-text-field v-model="form.name" label="Empresa *" outlined dense :rules="[required]"></v-text-field>
            <v-row dense>
              <v-col cols="6">
                <v-select v-model="form.colaborador_type" :items="COLABORADOR_TYPES" label="Tipo" outlined dense></v-select>
              </v-col>
              <v-col cols="6"><v-text-field v-model="form.cif" label="CIF" outlined dense></v-text-field></v-col>
            </v-row>
            <v-text-field v-model="form.email" label="Email de contacto" outlined dense></v-text-field>
            <div class="text-subtitle-2 mb-1">Condiciones *</div>
            <div class="d-flex">
              <v-checkbox v-for="c in CONDITIONS" :key="c.value" v-model="form[c.value]" :label="c.text" class="mr-6 mt-0" hide-details></v-checkbox>
            </div>
            <div class="text-caption error--text mb-3" v-if="!hasCondition">Indica al menos una condición</div>
            <div v-else class="mb-3"></div>
            <v-autocomplete v-model="form.dealer_ids" :items="dealers" item-text="name" item-value="id"
              label="Dealers para los que trabaja *" outlined dense multiple chips small-chips deletable-chips
              :rules="[v => (v && v.length > 0) || 'Indica al menos un dealer']"></v-autocomplete>

            <template v-if="!editing">
              <v-alert type="info" text dense>
                Si el CIF ya existe como colaborador (p. ej. trabaja para otro grupo) se reutiliza y solo se vincula a tus dealers.
              </v-alert>
              <!-- <v-checkbox v-model="withAdmin" label="Crear el usuario principal del colaborador" hide-details class="mt-0 mb-2"></v-checkbox> -->
              <template>
                <v-row dense>
                  <v-col cols="6"><v-text-field v-model="admin.name" label="Nombre *" outlined dense :rules="[required]"></v-text-field></v-col>
                  <v-col cols="6"><v-text-field v-model="admin.lastname" label="Apellidos *" outlined dense :rules="[required]"></v-text-field></v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="6"><v-text-field v-model="admin.email" label="Email *" outlined dense :rules="[required]"></v-text-field></v-col>
                  <v-col cols="6"><v-text-field v-model="admin.phone" label="Teléfono" outlined dense></v-text-field></v-col>
                </v-row>
              </template>
            </template>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!valid || !hasCondition" :loading="saving" @click="save">
            <v-icon left>mdi-content-save</v-icon>
            {{ editing ? 'Guardar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import GrupoPanelService from '@/services/GrupoPanelService'
import { COLABORADOR_TYPES, CONDITIONS, STATUS_ACTIVE, STATUS_INACTIVE, colaboradorTypeLabel, conditionLabel, apiData, apiError } from '@/grupo'

const emptyForm = () => ({ name: '', colaborador_type: null, cif: '', email: '', vn: false, vo: false, taller: false, dealer_ids: [] })
const emptyAdmin = () => ({ name: '', lastname: '', email: '', phone: '' })

export default {
  name: 'GrupoColaboradores',

  data: () => ({
    COLABORADOR_TYPES,
    CONDITIONS,
    STATUS_ACTIVE,
    colaboradores: [],
    dealers: [],
    dealerFilter: null,
    loading: false,
    dialog: false,
    editing: null,
    valid: false,
    saving: false,
    withAdmin: false,
    form: emptyForm(),
    admin: emptyAdmin(),
    headers: [
      { text: 'Empresa', value: 'name' },
      { text: 'Tipo', value: 'colaborador_type' },
      { text: 'CIF', value: 'cif' },
      { text: 'Email', value: 'email' },
      { text: 'Condiciones', value: 'conditions', sortable: false },
      { text: 'Dealers', value: 'dealers', sortable: false },
      { text: 'Estado', value: 'status' },
      { text: '', value: 'action', sortable: false, align: 'end' }
    ]
  }),

  mounted () {
    this.loadDealers()
    this.load()
  },

  computed: {
    hasCondition () {
      return CONDITIONS.some(c => this.form[c.value])
    }
  },

  methods: {
    colaboradorTypeLabel,
    conditionLabel,
    required: v => !!v || 'Obligatorio',

    async loadDealers () {
      try {
        this.dealers = apiData(await GrupoPanelService.dealers()) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los concesionarios'))
      }
    },

    async load () {
      this.loading = true
      try {
        const params = this.dealerFilter ? { dealer_id: this.dealerFilter } : {}
        this.colaboradores = apiData(await GrupoPanelService.colaboradores(params)) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los colaboradores'))
      } finally {
        this.loading = false
      }
    },

    viewDetails (colaborador) {
      this.$router.push({ name: 'GrupoColaboradorDetail', params: { colaborador_id: colaborador.id } })
    },

    async toggleStatus (colaborador) {
      const activate = colaborador.status !== STATUS_ACTIVE
      const ok = await this.$confirm(activate
        ? `¿Habilitar a ${colaborador.name}? Sus usuarios podrán volver a acceder y operar en los dealers del grupo.`
        : `¿Deshabilitar a ${colaborador.name}? Ni el colaborador ni sus usuarios podrán acceder ni operar en los dealers del grupo. Se conserva todo su histórico y podrás volver a habilitarlo.`,
      { title: activate ? 'Habilitar colaborador' : 'Deshabilitar colaborador' })
      if (!ok) return
      try {
        await GrupoPanelService.setColaboradorStatus(colaborador.id, activate ? STATUS_ACTIVE : STATUS_INACTIVE)
        this.$toast.success(activate ? 'Colaborador habilitado' : 'Colaborador deshabilitado')
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo cambiar el estado'))
      }
    },

    openForm (colaborador) {
      this.editing = colaborador
      this.withAdmin = false
      this.admin = emptyAdmin()
      this.form = colaborador ? {
        name: colaborador.name,
        colaborador_type: colaborador.colaborador_type,
        cif: colaborador.cif,
        email: colaborador.email,
        vn: colaborador.conditions.includes('vn'),
        vo: colaborador.conditions.includes('vo'),
        taller: colaborador.conditions.includes('taller'),
        dealer_ids: colaborador.dealers.map(d => d.id)
      } : emptyForm()
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },

    async save () {
      this.saving = true
      try {
        if (this.editing) {
          await GrupoPanelService.updateColaborador(this.editing.id, this.form)
          this.$toast.success('Colaborador actualizado')
        } else {
          const payload = { ...this.form }
          payload.admin = this.admin
          await GrupoPanelService.createColaborador(payload)
          this.$toast.success('Colaborador guardado')
        }
        this.dialog = false
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo guardar el colaborador'))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
